import React, { useRef, useLayoutEffect, useState } from "react";
import { Box } from "@mui/material";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { alertGroupedData, explanatoryNote, reportForm } from "./Report";
import { ReportLegend } from "./ReportLegend";
import { useThemeMode } from "Contexts/ThemeContext";

type AlertChartProps = {
  alertGroupedData: alertGroupedData[];
  reportForm: reportForm;
  legend: explanatoryNote[];
  chartHeight: number;
  filteredLegend: explanatoryNote[];
  setFilteredLegend: React.Dispatch<React.SetStateAction<explanatoryNote[]>>;
};

export const ReportAlertChart: React.FC<AlertChartProps> = ({
  alertGroupedData,
  reportForm,
  legend,
  chartHeight,
  filteredLegend,
  setFilteredLegend,
}) => {

  const alertChartRef = useRef<HTMLDivElement>(null);

  const [alertSum, setAlertSum] = useState<number>(0);

  const { mode } = useThemeMode();

  let additionalInfo: string | null = null;

  if (reportForm.unit === "1" && reportForm.targetId) {
    additionalInfo = reportForm.targetId;
  } else if (reportForm.unit === "1" && reportForm.targetId === "") {
    additionalInfo = "全シリアルNo";
  } else if (reportForm.unit === "2" && reportForm.targetName) {
    additionalInfo = reportForm.targetName;
  } else if (reportForm.unit === "2" && reportForm.targetName === "") {
    additionalInfo = "全エリア";
  }

  const formatDate = (dateString: string | null | undefined): string | null => {
    if (!dateString) {
      return null;
    }
    const [year, month, day] = dateString.split("-");
    return `${year}年${month}月${day}日`;
  };

  let period: string | null =
    formatDate(reportForm.startDate) + "～" + formatDate(reportForm.endDate);

  useLayoutEffect(() => {
    if (!alertChartRef.current) return;
    setAlertSum(
      alertGroupedData.reduce((sum, group) => {
        return (
          sum +
          group.tasks.reduce((taskSum, task) => taskSum + task.alertNum, 0)
        );
      }, 0)
    );

    // 色の定義
    const labelColor = am5.color(mode === "dark" ? 0xffffff : 0x4b0000);
    const root = am5.Root.new(alertChartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);

    // XYChart を作成
    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelX: "panX",
        wheelY: "zoomX",
        paddingBottom: 40, // X軸タイトル用の余白
        paddingLeft: 60, // Y軸タイトル用の余白
      })
    );

    // panX: true,
    // panY: true,
    // wheelX: "panX",
    // wheelY: "zoomX",
    // wheelX: "none",
    // wheelY: "none",

    // X軸（値軸）: alertNum の合計値（積み上げ用）
    const xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 50 }),
        min: 0,
        strictMinMax: true,
        maxPrecision: 0, // 小数点を表示しない
        extraMin: 0,
        extraMax: 0,
      })
    );

    // X軸のラベルを整数のみにする
    xAxis.get("renderer").labels.template.setAll({
      fill: labelColor,
      text: "{valueX.formatNumber('#')}", // 小数点なし
    });

    // X軸のグリッド設定
    xAxis.get("renderer").grid.template.setAll({
      strokeOpacity: 0.5,
      location: 0,
    });

    // X軸のラベルを整数にする
    xAxis
      .get("renderer")
      .labels.template.adapters.add("text", (text, target) => {
        const dataItem = target.dataItem as any;
        if (dataItem) {
          const value = dataItem.get("value");
          if (value !== undefined) {
            return Math.round(value).toString(); // 整数化
          }
        }
        return text;
      });

    xAxis.get("renderer").labels.template.adapters.add("text", (text) => {
      const v = Number(text);
      // もし数値にパースできて負の値だったら、ラベルを消す
      return v < 0 ? "" : text;
    });

    // Y軸（カテゴリー軸）: 作業者グループの yDisp
    const yAxis = chart.yAxes.push(
      am5xy.CategoryAxis.new(root, {
        categoryField: "yDisp",
        renderer: am5xy.AxisRendererY.new(root, {}),
      })
    );

    yAxis.get("renderer").labels.template.setAll({
      fill: labelColor,
    });

    const stackedData = alertGroupedData.map((worker: any) => {
      const data: any = { yDisp: worker.yDisp };
      legend.forEach((l) => {
        // legend.areaId を "/" で分割して、対象とする areaId の配列を作成
        const allowedIds = l.areaId.split("/");
        // record.tasks の中から、タスクの area が legend.areaName と一致し、かつ task.areaId が allowedIds に含まれるものの alertNum を合計する
        const sum = worker.tasks
          .filter(
            (task: any) =>
              task.area === l.areaName && allowedIds.includes(task.areaId)
          )
          .reduce((acc: any, task: any) => acc + task.alertNum, 0);

        // 出力キーは「エリア名_連結したareaId」とする
        const key = `${l.areaName}_${l.areaId}`;
        data[key] = sum;
      });
      return data;
    });

    //  legend をフィルタリング
    const keysWithData = new Set();
    stackedData.forEach((workerData) => {
      Object.keys(workerData).forEach((key) => {
        if (key !== "yDisp" && workerData[key]) {
          keysWithData.add(key);
        }
      });
    });
    setFilteredLegend(
      legend.filter((l) => {
        const key = `${l.areaName}_${l.areaId}`;
        return keysWithData.has(key);
      })
    );

    yAxis.data.setAll(stackedData);

    legend.forEach((leg: any) => {
      const series = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          xAxis: xAxis,
          yAxis: yAxis,
          stacked: true,
          valueXField: `${leg.areaName}_${leg.areaId}`,
          categoryYField: "yDisp",
        })
      );

      series.columns.template.setAll({
        fill: am5.color(leg.areaColor),
        stroke: am5.color(leg.areaColor),
      });

      // アラート時間のツールチップ
      series.columns.template.set(
        "tooltipText",
        `[bold]エリア：[/] ${leg.areaName}
[bold]シリアルNo：[/] ${leg.areaId}
[bold]アラート回数：{valueX}`
      );

      series.data.setAll(stackedData);
    });

    // X軸のタイトル
    chart.plotContainer.children.push(
      am5.Label.new(root, {
        text: "アラート回数",
        fontSize: 14,
        fontWeight: "bold",
        textAlign: "center",
        x: am5.p50, // X軸の中央
        y: am5.p100, // チャートの一番下に配置
        centerX: am5.p50,
        paddingTop: 30, // X軸との間隔
        fill: labelColor,
      })
    );

    // Y軸のタイトル
    chart.plotContainer.children.push(
      am5.Label.new(root, {
        text: "作業者名（タグID）", // 各文字を改行
        fontSize: 14,
        fontWeight: "bold",
        textAlign: "center",
        rotation: 0, // 縦書きにする
        y: -20, // チャートの中央に配置
        x: -100, // チャートの左端
        centerY: am5.p50,
        paddingRight: 10,
        fill: labelColor,
      })
    );

    chart.set(
      "scrollbarX",
      am5.Scrollbar.new(root, { orientation: "horizontal" })
    );
    chart.set(
      "scrollbarY",
      am5.Scrollbar.new(root, { orientation: "vertical" })
    );

    // Y軸の表示範囲を設定
    // const totalCategories = alertGroupedData.length + 2;
    // const visibleFraction = totalCategories > 6 ? 6 / totalCategories : 1;
    // yAxis.set("start", 0);
    // yAxis.set("end", visibleFraction);
    // Y軸の表示範囲を設定
    // 初期表示は5人分にする
    const visibleWorkerCount = 5;
    const totalCategories = stackedData.length;

    if (totalCategories > visibleWorkerCount) {
      yAxis.set("start", 0);
      yAxis.set("end", visibleWorkerCount / totalCategories);
    } else {
      yAxis.set("start", 0);
      yAxis.set("end", 1);
    }

    return () => {
      root.dispose();
    };
  }, [alertGroupedData, reportForm, legend, mode, setFilteredLegend]);

  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        {alertSum > 0 && (
          <ReportLegend reportForm={reportForm} legend={filteredLegend} />
        )}
        <Box sx={{ mt: 1.5, mr: 3 }}>合計警告数：{alertSum}</Box>
      </Box>
      <Box
        ref={alertChartRef}
        sx={{
          minWidth: "700px",
          width: "100%",
          height: {
            xs: "500px",
            sm: chartHeight - 40,
            md: chartHeight - 40,
            lg: chartHeight,
          },
          overflowY: "auto",
          display: "block",
        }}
      />
    </>
  );
};
